import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useDebouncedValue } from '@hooks/useDebouncedValue';
import { documentService } from '../../services/document.service';
import DocumentPickerDialog from './document-selector/DocumentPickerDialog';
import SelectedDocumentCard from './document-selector/SelectedDocumentCard';
import { DocumentItem, matchesQuery } from './document-selector/documentItem';

const DOCUMENT_SEARCH_DEBOUNCE_MS = 300;
const DOCUMENT_PAGE_SIZE = 50;

interface DocumentSelectorProps {
  label: string;
  selectedDocumentId?: string;
  selectedFileName?: string;
  contentPreview?: string;
  onSelect: (documentId: string, content: string, fileName: string, title: string) => void;
  onClear: () => void;
  error?: string;
  disabled?: boolean;
}

export const DocumentSelector: React.FC<DocumentSelectorProps> = ({
  label,
  selectedDocumentId,
  selectedFileName,
  contentPreview,
  onSelect,
  onClear,
  error,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [documents, setDocuments] = useState<DocumentItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loadedPage, setLoadedPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebouncedValue(searchQuery, DOCUMENT_SEARCH_DEBOUNCE_MS);
  const effectiveSearch = searchQuery ? debouncedSearch : '';
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [contentLoading, setContentLoading] = useState(false);
  const latestFetchId = useRef(0);

  // Search is evaluated against the tenant corpus by the API. Results stay
  // paginated so a broad term cannot freeze the dialog; the user can load every
  // additional page and always sees the server's total.
  const fetchDocuments = useCallback(async (page = 1, append = false) => {
    const fetchId = latestFetchId.current + 1;
    latestFetchId.current = fetchId;
    if (append) setLoadingMore(true);
    else {
      setLoading(true);
      setLoadingMore(false);
    }
    setFetchError(null);
    try {
      const response = await documentService.getDocuments({
        page,
        pageSize: DOCUMENT_PAGE_SIZE,
        sortBy: 'date',
        sortOrder: 'desc',
        search: effectiveSearch || undefined,
      });
      if (fetchId === latestFetchId.current) {
        const nextDocuments = response.results || [];
        setDocuments((current) => {
          if (!append) return nextDocuments;
          const existingIds = new Set(current.map((doc) => doc.id));
          return [...current, ...nextDocuments.filter((doc) => !existingIds.has(doc.id))];
        });
        setTotalCount(response.total ?? nextDocuments.length);
        setLoadedPage(page);
      }
    } catch (err) {
      console.error('Failed to fetch documents:', err);
      if (fetchId === latestFetchId.current) {
        if (!append) {
          setDocuments([]);
          setTotalCount(0);
          setLoadedPage(0);
        }
        setFetchError(t('drbfm.documentSelector.fetchError', 'ドキュメント一覧の取得に失敗しました'));
      }
    } finally {
      if (fetchId === latestFetchId.current) {
        if (append) setLoadingMore(false);
        else setLoading(false);
      }
    }
  }, [effectiveSearch, t]);

  useEffect(() => {
    if (dialogOpen) {
      fetchDocuments(1, false);
    }
  }, [dialogOpen, fetchDocuments]);

  const filteredDocuments = searchQuery === effectiveSearch
    ? documents
    : documents.filter((doc) => matchesQuery(doc, searchQuery));
  const searchSettled = searchQuery === effectiveSearch;
  const visibleTotalCount = searchSettled ? totalCount : filteredDocuments.length;
  const canLoadMore = searchSettled && documents.length < totalCount;

  const handleOpenDialog = () => {
    if (!disabled) {
      setDialogOpen(true);
    }
  };

  const handleCloseDialog = () => {
    latestFetchId.current += 1;
    setDialogOpen(false);
    setSearchQuery('');
    setLoading(false);
    setLoadingMore(false);
    setFetchError(null);
  };

  const handleLoadMore = () => {
    fetchDocuments(loadedPage + 1, true);
  };

  const handleDocumentSelect = async (doc: DocumentItem) => {
    setContentLoading(true);
    try {
      const contentResponse = await documentService.getDocumentContent(doc.id);
      onSelect(
        doc.id,
        contentResponse.content,
        contentResponse.file_name || doc.fileName || doc.title,
        contentResponse.title || doc.title
      );
      handleCloseDialog();
    } catch (err) {
      console.error('Failed to fetch document content:', err);
      setFetchError(t('drbfm.documentSelector.contentError', 'ドキュメントの内容を取得できませんでした'));
    } finally {
      setContentLoading(false);
    }
  };

  const handleClear = () => {
    onClear();
  };

  return (
    <div>
      {selectedDocumentId ? (
        <SelectedDocumentCard
          selectedFileName={selectedFileName}
          contentPreview={contentPreview}
          disabled={disabled}
          onChange={handleOpenDialog}
          onClear={handleClear}
        />
      ) : (
        <Button
          variant="outline"
          onClick={handleOpenDialog}
          disabled={disabled}
          className={cn(
            'h-auto w-full justify-start gap-2 border-dashed py-3',
            error
              ? 'border-solid border-destructive text-destructive'
              : 'border-border text-muted-foreground',
          )}
        >
          <FileText strokeWidth={1.9} />
          <span className="revy-cjk">{label}</span>
        </Button>
      )}

      {error && (
        <p className="revy-cjk mt-1 text-xs leading-[1.5] text-destructive">{error}</p>
      )}

      <DocumentPickerDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!open) handleCloseDialog();
        }}
        documents={filteredDocuments}
        selectedDocumentId={selectedDocumentId}
        searchQuery={searchQuery}
        onSearchQueryChange={setSearchQuery}
        loading={loading}
        loadingMore={loadingMore}
        totalCount={visibleTotalCount}
        canLoadMore={canLoadMore}
        contentLoading={contentLoading}
        fetchError={fetchError}
        onLoadMore={handleLoadMore}
        onSelect={handleDocumentSelect}
        onCancel={handleCloseDialog}
      />
    </div>
  );
};

export default DocumentSelector;
